/**
 * @fileoverview CATMS Crew PWA - IndexedDB Offline Storage (Dexie.js)
 * @description Offline-first data persistence for timestamp records
 */

import Dexie, { type Table } from 'dexie';

/** Offline timestamp record */
export interface OfflineTimestamp {
  id?: number;
  localId: string;
  timestampType: string;
  recordedAt: string;
  latitude: number | null;
  longitude: number | null;
  timestampSource: string;
  communicationType: string | null;
  workSessionId: string | null;
  vesselId: string | null;
  workType: string | null;
  note: string | null;
  synced: boolean;
  createdAt: string;
}

/** Offline work session */
export interface OfflineWorkSession {
  id?: number;
  localId: string;
  vesselId: string;
  targetDate: string;
  startedAt: string;
  endedAt: string | null;
  sessionStatus: string;
  workType: string | null;
  synced: boolean;
}

/** Sync queue entry */
export interface SyncQueueEntry {
  id?: number;
  type: 'timestamp' | 'session';
  payload: string;
  createdAt: string;
  retryCount: number;
}

class CatmsOfflineDB extends Dexie {
  timestamps!: Table<OfflineTimestamp>;
  workSessions!: Table<OfflineWorkSession>;
  syncQueue!: Table<SyncQueueEntry>;

  constructor() {
    super('catms-crew-offline');
    this.version(1).stores({
      timestamps: '++id, localId, synced, recordedAt',
      workSessions: '++id, localId, synced, targetDate',
      syncQueue: '++id, type, createdAt',
    });
  }
}

export const offlineDb = new CatmsOfflineDB();

/** Save offline timestamp */
export async function saveOfflineTimestamp(data: Omit<OfflineTimestamp, 'id' | 'synced' | 'createdAt'>): Promise<number> {
  return offlineDb.timestamps.add({
    ...data,
    synced: false,
    createdAt: new Date().toISOString(),
  });
}

/** Get unsynced timestamps */
export async function getUnsyncedTimestamps(): Promise<OfflineTimestamp[]> {
  return offlineDb.timestamps.where('synced').equals(0).toArray();
}

/** Mark timestamps as synced */
export async function markTimestampsSynced(ids: number[]): Promise<void> {
  await offlineDb.timestamps.where('id').anyOf(ids).modify({ synced: true });
}

/** Get unsynced count */
export async function getUnsyncedCount(): Promise<number> {
  return offlineDb.timestamps.where('synced').equals(0).count();
}

/** Save offline work session */
export async function saveOfflineSession(data: Omit<OfflineWorkSession, 'id' | 'synced'>): Promise<number> {
  return offlineDb.workSessions.add({ ...data, synced: false });
}

/** Clear all synced data */
export async function clearSyncedData(): Promise<void> {
  await offlineDb.timestamps.where('synced').equals(1).delete();
  await offlineDb.workSessions.where('synced').equals(1).delete();
}
